import { alpha, Divider, Stack, type Theme, ToggleButton } from '@mui/material';
import {
    ChartSpline,
    Diameter,
    type LucideIcon,
    RectangleHorizontal,
    Ruler,
    Triangle,
} from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import SectionLabel from '~/components/SectionLabel';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import {
    isRegionMode,
    MEASURE_FAMILY_OF,
    MEASURE_MODES,
    MeasureMode,
} from '~/utils/annotationKinds';

/** The icon each mode is shown with, in the picker and in the hint. */
export const MEASURE_MODE_ICON = {
    distance: Ruler,
    angle: Triangle,
    profile: ChartSpline,
    circle: Diameter,
    rectangle: RectangleHorizontal,
} as const satisfies Record<MeasureMode, LucideIcon>;

/** Each family's caption and its modes, in the tray's order. */
const GROUPS = [
    ...new Set(MEASURE_MODES.map((mode) => MEASURE_FAMILY_OF[mode])),
].map(
    (family) =>
        [
            `measure.group.${family}`,
            MEASURE_MODES.filter((mode) => MEASURE_FAMILY_OF[mode] === family),
        ] as const
);

/** Each button grows from its label's width, so a long label is never cut. */
const MODE_BUTTON_SX = {
    flex: '1 1 auto',
    minWidth: 0,
    flexDirection: 'column',
    gap: 0.5,
    py: 0.75,
    px: 0.5,
    fontSize: 11,
    border: 1,
    borderColor: 'divider',
    borderRadius: '6px',
    '&.Mui-selected, &.Mui-selected:hover': {
        color: 'primary.main',
        bgcolor: ({ palette }: Theme) => alpha(palette.primary.main, 0.15),
        borderColor: 'primary.main',
    },
} as const;

/** The Measure tool's modes in their two families, the armed one pressed. */
export default function MeasureModePicker({
    active,
    onSelect,
    regionRefusal,
}: {
    /** The mode armed now; null while another tool is active. */
    active: MeasureMode | null;
    onSelect: (mode: MeasureMode) => void;
    /** Why the regions are refused; undefined when they are offered. */
    regionRefusal: string | undefined;
}) {
    const modeButton = (mode: MeasureMode) => {
        const Icon = MEASURE_MODE_ICON[mode];
        const refusal = isRegionMode(mode) ? regionRefusal : undefined;
        return (
            // Always wrapped, so a flipping gate does not remount the button.
            // Sideways, clear of the rail above and the analysis header below.
            <DisabledHint
                key={mode}
                title={refusal}
                keepWrapper
                placement="right"
                spanStyle={{ display: 'flex', flex: '1 1 auto', minWidth: 0 }}
            >
                <ToggleButton
                    value={mode}
                    size="small"
                    selected={active === mode}
                    disabled={refusal !== undefined}
                    onChange={() => onSelect(mode)}
                    data-testid={`measure-mode-${mode}`}
                    sx={MODE_BUTTON_SX}
                >
                    <Icon size={18} />
                    <FormattedMessage id={`measure.mode.${mode}`} />
                </ToggleButton>
            </DisabledHint>
        );
    };

    return (
        <Stack
            direction="row"
            gap={1.25}
            px={1.5}
            pt={1}
            pb={1.25}
            divider={<Divider orientation="vertical" flexItem />}
        >
            {GROUPS.map(([captionId, modes]) => (
                <Stack key={captionId} flex="1 1 auto" minWidth={0} gap={0.75}>
                    <SectionLabel
                        id={captionId}
                        sx={{
                            lineHeight: '20px',
                            letterSpacing: '0.08em',
                            color: 'text.disabled',
                        }}
                    />
                    <Stack direction="row" gap={0.75}>
                        {modes.map(modeButton)}
                    </Stack>
                </Stack>
            ))}
        </Stack>
    );
}
